/* ---------------------------------------
   sangria-plp-v3.js
   Handles loading product cards dynamically
   with hover variant switching and event tracking.
------------------------------------------ */

const amplifyURL = 'https://aurate-prodin.sangria.tech'

/* Initialize once DOM is ready */
document.addEventListener('DOMContentLoaded', async () => {
  await loadProductCards()
})

/**
 * Load product cards into the shoppable container
 * @param {boolean} retry - whether this is a retry attempt
 */
async function loadProductCards(retry = false) {
  try {
    const slug = window.location.pathname.split('/').pop()
    const searchInputElement = document.getElementById(
      'sg-isShoppableCollectionValue'
    )
    const searchValue = searchInputElement
      ? searchInputElement.value.trim()
      : ''

    // Build API URL
    const baseUrl = `${amplifyURL}/blogs/shopifyplp/${slug}`
    const url = searchValue
      ? `${baseUrl}?collectionId=${encodeURIComponent(searchValue)}`
      : baseUrl

    const response = await fetch(url)
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

    // Parse returned HTML
    const productCardHtml = await response.text()
    const tempContainer = document.createElement('div')
    tempContainer.innerHTML = productCardHtml

    const productCards = tempContainer.querySelectorAll(
      '#sg-engagement-section #sg-aurate-product-card'
    )
    const isShoppableContainer = document.getElementById(
      'sg-isShoppablePlpContainer'
    )

    // Retry once if no cards found
    if (!productCards.length && !retry) {
      console.warn('No product cards found. Retrying after 500ms...')
      setTimeout(() => loadProductCards(true), 500)
      return
    }
    // Append product cards and initialize variants
    if (isShoppableContainer) {
      productCards.forEach((card) => {
        const cardClone = card.cloneNode(true)
        isShoppableContainer.appendChild(cardClone)
        initializeVariantHandling(cardClone)
      })
    }

    await loadDynamicScripts(tempContainer)
  } catch (error) {
    console.error('Error loading product cards:', error)
  }
}

/**
 * Initialize hover handling for product variant images
 * @param {HTMLElement} card - product card element
 */
function initializeVariantHandling(card) {
  const hiddenJson = card.querySelector('#sg-aurate-variant-id-values')
  const prodImg = card.querySelector('#sg-aurate-product-image')
  const prodImgContainer = card.querySelector(
    '#sg-aurate-product-image-container'
  )

  if (!hiddenJson || !prodImg || !prodImgContainer) return

  let defaultImg = null
  let modelImg = null
  let isProcessing = false
  let firstVariantImages = null
  try {
    const variants = JSON.parse(hiddenJson.value.replace(/&quot;/g, '"'))
    if (!variants?.length) return

    firstVariantImages = variants[0].featuredImage || []
    defaultImg = firstVariantImages[0]?.src ?? null

    const modelImgObj =
      firstVariantImages?.length > 1
        ? firstVariantImages[firstVariantImages?.length - 1] || []
        : []
    modelImg = modelImgObj?.src ?? null

    // Preload model image for smoother hover
    if (modelImg && modelImg !== defaultImg) {
      const preloadLink = document.createElement('link')
      preloadLink.rel = 'preload'
      preloadLink.as = 'image'
      preloadLink.href = modelImg
      document.head.appendChild(preloadLink)
    }
  } catch (e) {
    console.error('[Variant] JSON parse failed', e)
    return
  }

  // Hover handlers (optimized with requestAnimationFrame)
  const handleMouseEnter = () => {
    if (isProcessing || !modelImg) return
    isProcessing = true
    requestAnimationFrame(() => {
      prodImg.src = modelImg
      prodImg.style.cssText =
        'object-fit: cover; transform: scale(1.05); height: 100%; transition: transform 0.3s ease;'
      isProcessing = false
    })
  }

  const handleMouseLeave = () => {
    if (isProcessing || !defaultImg) return
    isProcessing = true
    requestAnimationFrame(() => {
      prodImg.src = defaultImg
      prodImg.style.cssText =
        'object-fit: contain; transform: scale(1); transition: transform 0.3s ease;'
      isProcessing = false
    })
  }

  // Use passive event listeners for performance
  prodImgContainer.addEventListener('mouseenter', handleMouseEnter, {
    passive: true
  })
  prodImgContainer.addEventListener('mouseleave', handleMouseLeave, {
    passive: true
  })
}

/**
 * Load and execute dynamic scripts from fetched HTML
 */
async function loadDynamicScripts(container) {
  container.querySelectorAll('script').forEach((script) => {
    const newScript = document.createElement('script')
    if (script.src) {
      newScript.src = script.src
    } else {
      newScript.textContent = script.textContent
    }
    document.body.appendChild(newScript)
  })
}

/* ---------------------------------------
   Analytics and Tracking
------------------------------------------ */

/**
 * Fire custom analytics event
 */
function customEvent(action, event_label = '', event_title = '', value = 0) {
  gtag('event', action, { event_label, event_title, value })
}

/**
 * Handle click tracking
 */
function handleClickEvents(action, event_title, label = '') {
  const from = window.location.href
  let event_label =
    label ||
    (from.includes('/blogs') ? 'Blog' : from.includes('/pages') ? 'PLP' : '')
  customEvent(action, event_label, event_title)
}
